import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { AlertCircle, Lock, Mail, ShieldCheck, ArrowLeft, ArrowRight, Eye, EyeOff } from "lucide-react";
import { apiCall, login, type ApiError } from "@/lib/api";
import { useAuthStore } from "@/lib/auth";
import { apexDe, useBranding } from "@/lib/branding";
import "@/lib/theme"; // rc146 — aplica claro/oscuro también antes del login

// rc146 — Login dedicado al ISP (tema Glass Zafiro, claro y oscuro).
// Decisión Alex 11-04: nada de página de producto. Se quitó el hero de
// funciones, las etiquetas de integraciones (TOKU/SPEI/SAT/MIKROTIK), el
// indicador "conexión segura", el número de versión y el dominio del pie:
// el login está expuesto a internet y no debe anunciar qué corre detrás.
// Arriba solo la marca del ISP (GET /api/v1/branding: nombre y logo de
// Ajustes). La LÓGICA de auth (credenciales → TFA → change-password) es la
// misma de siempre; solo cambió la presentación.

type MeResponse = {
  id: string;
  email: string;
  nombre_completo: string | null;
  rol: { id: string; nombre: string; tipo: string } | null;
  permissions: string[];
  tfa_enabled: boolean;
  last_login_at: string | null;
};

const schema = z.object({
  email: z.string().email("Email inválido"),
  password: z.string().min(1, "Ingresa tu password"),
});

type FormValues = z.infer<typeof schema>;

type Step = "credentials" | "tfa";

export function LoginPage() {
  const navigate = useNavigate();
  const { data: brand } = useBranding();
  const nombre = (brand?.nombre || "").trim();
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState<Step>("credentials");
  const [tfaCode, setTfaCode] = useState("");
  const [showPass, setShowPass] = useState(false);
  // F107 — guardamos credenciales del paso 1 para reusar en paso 2
  const [stashedCreds, setStashedCreds] = useState<FormValues | null>(null);

  const setAccessToken = useAuthStore((s) => s.setAccessToken);
  const setChangeToken = useAuthStore((s) => s.setChangeToken);
  const setUser = useAuthStore((s) => s.setUser);
  const setPermissions = useAuthStore((s) => s.setPermissions);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: "", password: "" },  // rc129: sin cuenta prellenada
  });

  async function processLoginResponse(
    resp: Awaited<ReturnType<typeof login>>,
    creds: FormValues,
  ) {
    if (resp.token_type === "bearer") {
      setAccessToken(resp.access_token);
      try {
        const me = await apiCall<MeResponse>("get", "/api/v1/me");
        setUser({
          id: me.id, email: me.email, nombre_completo: me.nombre_completo,
          rol: me.rol ? { id: me.rol.id, nombre: me.rol.nombre } : null,
        });
        setPermissions(me.permissions);
      } catch {
        setPermissions([]);
      }
      navigate("/", { replace: true });
    } else if (resp.token_type === "password_change_required") {
      setChangeToken(resp.token);
      navigate("/change-password", {
        replace: true,
        state: { email: creds.email, currentPassword: creds.password },
      });
    } else if (resp.token_type === "tfa_challenge") {
      // F107 — pasar al paso 2
      setStashedCreds(creds);
      setStep("tfa");
      setError(null);
    } else {
      setError("Respuesta inesperada del servidor.");
    }
  }

  async function onSubmitCreds(values: FormValues) {
    setError(null);
    setSubmitting(true);
    try {
      const resp = await login(values.email, values.password);
      await processLoginResponse(resp, values);
    } catch (e) {
      const api = e as ApiError;
      setError(api.message ?? "Error al iniciar sesión");
    } finally {
      setSubmitting(false);
    }
  }

  async function onSubmitTfa(e: React.FormEvent) {
    e.preventDefault();
    if (!stashedCreds || !tfaCode.trim()) return;
    setError(null);
    setSubmitting(true);
    try {
      const resp = await login(
        stashedCreds.email,
        stashedCreds.password,
        tfaCode.trim(),
      );
      await processLoginResponse(resp, stashedCreds);
    } catch (e) {
      const api = e as ApiError;
      setError(api.message ?? "Código inválido");
    } finally {
      setSubmitting(false);
    }
  }

  function backToCredentials() {
    setStep("credentials");
    setTfaCode("");
    setStashedCreds(null);
    setError(null);
  }

  const inputCls =
    "h-[38px] w-full rounded-[9px] border border-input bg-background/60 pl-[34px] pr-3 text-[13px] text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-primary focus:ring-[3px] focus:ring-primary/25";
  const errBoxCls =
    "flex items-start gap-2 rounded-[9px] border border-danger/40 bg-danger/10 p-2.5 text-[11.5px] text-danger";
  const iconCls = "pointer-events-none absolute left-[11px] top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-muted-foreground";
  const labelCls = "mb-[5px] block text-[12px] font-medium text-foreground/85";
  const eyebrowCls = "text-[10px] font-semibold uppercase tracking-[.08em] text-electric";
  const primaryBtnCls =
    "flex h-[38px] w-full items-center justify-center gap-2 rounded-[9px] bg-primary text-[13px] font-semibold text-primary-foreground shadow-[0_6px_18px_hsl(var(--primary)/0.3)] transition-[filter] hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60";

  return (
    <div className="relative flex min-h-full flex-col items-center justify-center gap-5 px-4 pb-16 pt-10 text-foreground">
      {/* ── marca del ISP ── */}
      <div className="flex items-center gap-2.5">
        {brand?.logo_url ? (
          <img src={brand.logo_url} alt="" className="h-9 w-9 rounded-[10px] object-contain" />
        ) : (
          <div
            aria-hidden
            className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-gradient-to-b from-[#3b78f5] to-[#1d4ed8] text-[16px] font-bold text-white shadow-[inset_0_1px_0_rgba(255,255,255,.25),0_6px_18px_rgba(37,99,235,.35)]"
          >
            {nombre ? nombre.charAt(0).toUpperCase() : ""}
          </div>
        )}
        {nombre && <div className="text-[15px] font-bold leading-tight tracking-[.04em]">{nombre}</div>}
      </div>

      {/* ── tarjeta de acceso ── */}
      <div className="w-full max-w-[352px] rounded-2xl border border-border bg-card p-6 shadow-[0_18px_44px_rgba(0,0,0,.18)] backdrop-blur-xl">
        {step === "credentials" && (
          <form onSubmit={handleSubmit(onSubmitCreds)}>
            <div className={eyebrowCls}>Portal administrativo</div>
            <h1 className="mt-1 text-[19px] font-semibold tracking-[-.01em]">Inicia sesión</h1>
            <p className="mb-4 mt-0.5 text-[12.5px] text-muted-foreground">Continúa hacia tu centro de control.</p>

            <label htmlFor="email" className={labelCls}>Correo electrónico</label>
            <div className="relative">
              <Mail className={iconCls} />
              <input
                id="email"
                type="email"
                autoComplete="username"
                placeholder={`nombre@${apexDe(brand?.dominio_admin)}`}
                className={inputCls}
                {...register("email")}
              />
            </div>
            {errors.email && <span className="mt-1 block text-[11.5px] text-danger">{errors.email.message}</span>}

            <label htmlFor="password" className={`${labelCls} mt-3.5`}>Contraseña</label>
            <div className="relative">
              <Lock className={iconCls} />
              <input
                id="password"
                type={showPass ? "text" : "password"}
                autoComplete="current-password"
                placeholder="••••••••••••"
                className={`${inputCls} pr-[64px]`}
                {...register("password")}
              />
              <button
                type="button"
                onClick={() => setShowPass((v) => !v)}
                className="absolute right-1 top-1/2 flex h-[30px] -translate-y-1/2 items-center gap-1 rounded-[7px] px-2 text-[11.5px] font-semibold text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              >
                {showPass ? <EyeOff size={14} /> : <Eye size={14} />}
                {showPass ? "Ocultar" : "Ver"}
              </button>
            </div>
            {errors.password && <span className="mt-1 block text-[11.5px] text-danger">{errors.password.message}</span>}
            {error && (
              <div className={`${errBoxCls} mt-3`}>
                <AlertCircle size={14} className="mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}
            <button type="submit" disabled={submitting} className={`${primaryBtnCls} mt-4`}>
              {submitting ? "Verificando…" : "Entrar"}
              {!submitting && <ArrowRight size={14} />}
            </button>
          </form>
        )}
        {step === "tfa" && (
          <form onSubmit={onSubmitTfa}>
            <div className={eyebrowCls}>Verificación</div>
            <h1 className="mt-1 text-[19px] font-semibold tracking-[-.01em]">Dos factores</h1>
            <p className="mb-4 mt-0.5 text-[12.5px] text-muted-foreground">Un paso más para entrar.</p>
            <div className="mb-3.5 flex items-start gap-2 rounded-[10px] border border-primary/25 bg-primary/[0.08] p-2.5 text-[11.5px] text-foreground/80">
              <ShieldCheck size={14} className="mt-0.5 shrink-0 text-electric" />
              <span>
                Mete el código de 6 dígitos de tu Authenticator
                <span className="mt-0.5 block text-muted-foreground">
                  O un backup code (XXXX-XXXX) si perdiste el dispositivo.
                </span>
              </span>
            </div>
            <label htmlFor="tfa" className={labelCls}>Código</label>
            <input
              id="tfa"
              inputMode="text"
              autoComplete="one-time-code"
              value={tfaCode}
              onChange={(e) => setTfaCode(e.target.value)}
              placeholder="123456 o XXXX-XXXX"
              autoFocus
              className={`${inputCls} h-[40px] px-3 text-center font-mono text-[15px] tracking-[.1em]`}
            />
            {error && (
              <div className={`${errBoxCls} mt-3`}>
                <AlertCircle size={14} className="mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}
            <button type="submit" disabled={submitting || !tfaCode.trim()} className={`${primaryBtnCls} mt-4`}>
              {submitting ? "Verificando…" : "Verificar y entrar"}
            </button>
            <button
              type="button"
              onClick={backToCredentials}
              disabled={submitting}
              className="mt-1.5 flex h-8 w-full items-center justify-center gap-1.5 rounded-[9px] text-[12px] font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              <ArrowLeft size={12} /> Volver
            </button>
          </form>
        )}
        <div className="mt-5 border-t border-border pt-3 text-center text-[11.5px] text-muted-foreground">
          Acceso exclusivo para personal autorizado
        </div>
      </div>

      {nombre && (
        <div className="absolute inset-x-0 bottom-5 text-center text-[11px] text-muted-foreground">
          © {new Date().getFullYear()} {nombre}
        </div>
      )}
    </div>
  );
}
